/**
 * Combobox Component
 * Based on shadcn/ui + Figma design specification
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 *
 * Features: Search, Grouping, Icons, Description, Creatable
 * Sizes: Regular, Large, Small, Mini
 * Shapes: Default, Round
 * States: Default, Focus, Error, Disabled
 */

import { forwardRef, useState, useRef, useEffect, useMemo, ReactNode, InputHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { ChevronDown, Check, Search, Plus } from 'lucide-react';

export type ComboboxSize = 'mini' | 'small' | 'regular' | 'large';
export type ComboboxShape = 'default' | 'round';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface ComboboxGroup {
  label: string;
  options: ComboboxOption[];
}

export interface ComboboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  /** Options list or grouped options */
  options: ComboboxOption[] | ComboboxGroup[];
  /** Selected value */
  value?: string;
  /** Default value */
  defaultValue?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Size variant */
  size?: ComboboxSize;
  /** Shape variant */
  shape?: ComboboxShape;
  /** Error state */
  error?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Allow creating new items */
  creatable?: boolean;
  /** Called when creating new item */
  onCreate?: (value: string) => void;
  /** Called when value changes */
  onChange?: (value: string) => void;
  /** Additional className */
  className?: string;
}

const comboboxTriggerVariants = cva(
  'inline-flex items-center justify-between w-full border transition-all duration-200 cursor-pointer bg-white',
  {
    variants: {
      size: {
        mini: 'h-7 text-xs px-2 gap-1.5',
        small: 'h-8 text-sm px-2.5 gap-1.5',
        regular: 'h-10 text-sm px-3 gap-2',
        large: 'h-12 text-base px-4 gap-2',
      },
      shape: {
        default: 'rounded-md',
        round: 'rounded-full',
      },
      error: {
        true: 'border-danger focus-within:ring-2 focus-within:ring-danger/20',
        false: 'border-border hover:border-muted-foreground focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed bg-accent-0',
        false: '',
      },
    },
    defaultVariants: {
      size: 'regular',
      shape: 'default',
      error: false,
      disabled: false,
    },
  }
);

function isGrouped(options: ComboboxOption[] | ComboboxGroup[]): options is ComboboxGroup[] {
  return options.length > 0 && 'options' in options[0];
}

function flattenOptions(options: ComboboxOption[] | ComboboxGroup[]): ComboboxOption[] {
  if (isGrouped(options)) {
    return options.flatMap((group) => group.options);
  }
  return options;
}

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      options,
      value,
      defaultValue,
      placeholder = 'Select an item',
      searchPlaceholder = 'Search...',
      size = 'regular',
      shape = 'default',
      error = false,
      disabled = false,
      emptyMessage = 'No results found.',
      creatable = false,
      onCreate,
      onChange,
      className,
      ...props
    },
    _ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (value !== undefined) {
        setSelectedValue(value);
      }
    }, [value]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
      if (isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isOpen]);

    const allOptions = useMemo(() => flattenOptions(options), [options]);

    const filteredOptions = useMemo(() => {
      const query = searchQuery.toLowerCase();
      if (!query) return options;

      if (isGrouped(options)) {
        return options
          .map((group) => ({
            ...group,
            options: group.options.filter(
              (opt) =>
                opt.label.toLowerCase().includes(query) ||
                opt.description?.toLowerCase().includes(query)
            ),
          }))
          .filter((group) => group.options.length > 0);
      }

      return options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(query) ||
          opt.description?.toLowerCase().includes(query)
      );
    }, [options, searchQuery]);

    const handleSelect = (option: ComboboxOption) => {
      if (option.disabled) return;
      setSelectedValue(option.value);
      onChange?.(option.value);
      setIsOpen(false);
      setSearchQuery('');
    };

    const handleCreate = () => {
      if (!searchQuery.trim()) return;
      onCreate?.(searchQuery.trim());
      setIsOpen(false);
      setSearchQuery('');
    };

    const selectedOption = allOptions.find((opt) => opt.value === selectedValue);
    const hasNoResults = flattenOptions(filteredOptions).length === 0;
    const showCreateOption = creatable && searchQuery.trim() && hasNoResults;

    const renderOption = (option: ComboboxOption) => (
      <div
        key={option.value}
        onClick={() => handleSelect(option)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors text-sm',
          option.disabled && 'opacity-50 cursor-not-allowed',
          !option.disabled && 'hover:bg-accent-1',
          option.value === selectedValue && 'bg-primary/10'
        )}
      >
        {option.icon && <span className="text-muted-foreground">{option.icon}</span>}
        <div className="flex-1 min-w-0">
          <div className={cn(
            'truncate',
            option.value === selectedValue ? 'text-primary' : 'text-foreground'
          )}>
            {option.label}
          </div>
          {option.description && (
            <div className="text-xs text-muted-foreground truncate">{option.description}</div>
          )}
        </div>
        {option.value === selectedValue && (
          <Check className="w-4 h-4 text-primary flex-shrink-0" />
        )}
      </div>
    );

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={comboboxTriggerVariants({
            size,
            shape,
            error,
            disabled,
          })}
        >
          <span className={selectedOption ? 'text-foreground truncate' : 'text-muted-foreground'}>
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {isOpen && (
          <div
            className={cn(
              'absolute z-50 w-full mt-1 bg-white border border-accent-2 shadow-lg',
              shape === 'round' ? 'rounded-2xl' : 'rounded-md',
              'max-h-80 overflow-hidden'
            )}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-accent-2">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-accent-0 rounded-md">
                <Search className="w-4 h-4 text-muted-foreground" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                  {...props}
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-60 overflow-auto py-1">
              {isGrouped(filteredOptions as ComboboxGroup[])
                ? (filteredOptions as ComboboxGroup[]).map((group) => (
                    <div key={group.label}>
                      <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase">
                        {group.label}
                      </div>
                      {group.options.map(renderOption)}
                    </div>
                  ))
                : (filteredOptions as ComboboxOption[]).map(renderOption)}

              {hasNoResults && !showCreateOption && (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              )}

              {showCreateOption && (
                <div
                  onClick={handleCreate}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent-1 text-sm text-primary"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create "{searchQuery}"</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

Combobox.displayName = 'Combobox';
